import { Stack } from "@mui/material";
import LanguageContext from "components/LanguageContext";
import HelpBlock from "components/LayoutFront/HelpBlock";
import Share from "components/LayoutFront/Share";
import Wrapper from "components/LayoutFront/Wrapper";
import SiteContext from "components/SiteContext";
import PropTypes from "prop-types";
import React, { useContext, useMemo } from "react";
import t from "utils/locales/translation.json";

const displayStatus = {
  ALL: "ALL",
  SHARE: "SHARE",
  HELP: "HELP",
};

const HelpAndShare = (props) => {
  const { alwaysDisplayShare = false, alwaysDisplayHelp = false, template = "", mt = 5 } = props;
  const { language } = useContext(LanguageContext);
  const site = useContext(SiteContext);
  const { settings: siteSettings } = site || {};
  const {
    faqUrl,
    franceServicesUrl,
    faqUrlEN,
    franceServicesUrlEN,
    display = displayStatus.ALL,
    selectedTemplates,
  } = siteSettings || {};

  const faqUrlToDisplay = useMemo(() => (language === "EN" ? faqUrlEN : faqUrl), [faqUrl, faqUrlEN, language]);
  const franceServicesUrlToDisplay = useMemo(
    () => (language === "EN" ? franceServicesUrlEN : franceServicesUrl),
    [franceServicesUrl, franceServicesUrlEN, language],
  );

  const displayHelpAndShareBlock = useMemo(() => {
    if (selectedTemplates) {
      const selectedTemplatesList = JSON.parse(selectedTemplates);
      if (Array.isArray(selectedTemplatesList)) {
        return selectedTemplatesList.some((st) => st.key === template);
      }
    }
    return false;
  }, [selectedTemplates, template]);

  const displayShare = useMemo(
    () =>
      ([displayStatus.ALL, displayStatus.SHARE].includes(display) && displayHelpAndShareBlock) || alwaysDisplayShare,
    [alwaysDisplayShare, display, displayHelpAndShareBlock],
  );
  const displayHelp = useMemo(
    () => ([displayStatus.ALL, displayStatus.HELP].includes(display) && displayHelpAndShareBlock) || alwaysDisplayHelp,
    [alwaysDisplayHelp, display, displayHelpAndShareBlock],
  );

  const helpBlockList = useMemo(
    () => [
      {
        iconTitle: t[language].components.help_block_icon_title_1,
        icon: "questionsAnswers",
        title: t[language].components.help_block_title_1,
        link: faqUrlToDisplay,
      },
      {
        iconTitle: t[language].components.help_block_icon_title_2,
        icon: "helpIcon",
        title: t[language].components.help_block_title_2,
        link: franceServicesUrlToDisplay,
      },
    ],
    [faqUrlToDisplay, franceServicesUrlToDisplay, language],
  );

  if (displayHelpAndShareBlock && (alwaysDisplayHelp || alwaysDisplayShare)) {
    return null;
  }

  return (
    <Wrapper
      template={template}
      wrapperProps={{
        sx: {
          mt,
          "& .fr-follow .fr-grid-row": {
            "& > div": {
              "&:not(:last-of-type)": {
                pr: { md: 3 },
              },
              "&:not(:first-of-type)": {
                pl: { md: 3 },
                boxShadow: {
                  xs: "0 -2px 0 -1px var(--border-default-blue-france)",
                  md: "-1px 0 0 0 var(--border-default-blue-france)",
                },
              },
            },
            "& .helpClass": {
              pt: { xs: 6, md: 0 },
              pb: { xs: 3, md: 0 },
              "& > div": {
                alignItems: "center",
              },
            },
            "& * > div:not(.helpClass)": {
              justifyContent: "flex-start",
            },
            "& :first-of-type > .fr-share": {
              flexDirection: "column",
              alignItems: "flex-start",
            },
          },
          "& .fr-share__link": {
            mr: { md: 0, lg: 1 },
          },
        },
      }}
    >
      <div className="fr-follow">
        <div className="fr-container">
          <div className="fr-grid-row">
            {displayShare && (
              <div
                className={`fr-col-12 fr-col-md-${
                  display !== displayStatus.ALL || (!displayHelpAndShareBlock && alwaysDisplayShare) ? 12 : 4
                }`}
              >
                <Share />
              </div>
            )}
            {displayHelp &&
              helpBlockList.map((helpBlock) => (
                <Stack
                  key={helpBlock.title}
                  className={`helpClass fr-col-12 fr-col-md-${
                    display !== displayStatus.ALL || (!displayHelpAndShareBlock && alwaysDisplayHelp) ? 6 : 4
                  }`}
                  sx={{ alignItems: "center" }}
                >
                  <HelpBlock
                    key={helpBlock.title}
                    linkLabel={t[language].components.help_block_link_label}
                    {...helpBlock}
                  />
                </Stack>
              ))}
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

HelpAndShare.propTypes = {
  alwaysDisplayShare: PropTypes.bool,
  alwaysDisplayHelp: PropTypes.bool,
  template: PropTypes.string,
  mt: PropTypes.number,
};

export default HelpAndShare;
